import { useLocation, useNavigate } from "react-router";
import ReviewSessionGuard from "@features/review-mode/ReviewSessionGuard";
import { SkipLink } from "@/common/components/SkipLink";
import { SRAnnouncementProvider } from "@/state/providers/SRAnnouncementProvider";
import Navbar from "../features/navigation/Navbar";
import { useCallback, useEffect, useRef, useState } from "react";
import { ENDPOINTS } from "../common/utils/url-registry";
import { useSession } from "@supabase/auth-helpers-react";
import { useAccountContext } from "@providers/AccountProvider";
import { useImpersonation } from "@providers/ImpersonationProvider";
import { getStoredSelectedClinicAccountId } from "@/common/lib/selectedClinicAccountStorage";
import CacheBuster from "@providers/CacheBuster";
import { supabase } from "@/common/lib/supabaseClient";
import { signOutLocal } from "@/common/lib/signOut";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import NotificationBanner from "@/common/components/NotificationBanner";
import { AssetLibraryProvider } from "@/state/providers/AssetLibraryProvider";
import SetupFlow from "@/features/onboarding/SetupFlow";
import AppBlur from "@/routes/AppBlur";
import RecordingWidget from "@/features/recording-widget/RecordingWidget";
import { MergeVisitsDialogHost } from "@/features/merge-visits/MergeVisitsDialogHost";
import HappyDocMascot from "@/assets/hd-icons/happydoc-mascot-sticker.svg?react";
import { HALLOWEEN_SCOUT_SRC, isHalloween } from "@/common/utils/halloween";
import ScoutChat from "@/common/components/ScoutChat";
import PhoneCallWidget from "@/features/phone-call-widget/PhoneCallWidget";
import { TourProvider } from "@providers/TourProvider";
import TourEngine from "@/common/components/TourEngine";
import TourCrashFallback from "@/common/components/TourCrashFallback";
import TourDevTools from "@/tours/tour-dev-tools";
import TourBlockerRegistrations from "@/tours/TourBlockerRegistrations";
import XCloseIcon from "@icons/x-close.svg?react";
import PlanUpgradeModal from "@/features/settings/components/billing/PlanUpgradeModal";
import TrialExpiredOverlay from "@/features/settings/components/billing/TrialExpiredOverlay";
import { useBillingPlan } from "@/state/hooks/useBilling";
import { AccountStatus } from "@/common/utils/types";
import { useAppSelector } from "@/common/hooks/useRedux";
import { useMemberships } from "@/state/hooks/useMemberships";
import { useGroupSession } from "@/state/hooks/useGroupSession";
import { deriveGroupMembership } from "@/common/lib/groupUsageApi";
import { createCoalescingIdleRunner, hasAudioInFlight } from "@common/lib/recorderActivity";
import { setIntercomLauncherHidden } from "@common/lib/Intercom";
import { MicProvider } from "@/features/recording-widget/mic/MicProvider";
import { KeepEditsProvider } from "@/features/patient-view/components/edits/KeepEditsProvider";

type Props = {
    children: React.ReactElement | React.ReactElement[];
    allowedPermissions?: string[];
};

// EN-837: the banned-user sign-out is deferred until the recorder is idle. The runner is MODULE-LEVEL
// (not a per-instance ref) so a pending deferral survives a PermissionWrapper unmount: /corporate and
// /select-clinic sit OUTSIDE this wrapper, so redirecting there unmounts it — a ref-scoped runner
// cancelled on unmount would silently drop a still-pending ban sign-out and leave a banned user signed
// in. Single-slot coalescing means concurrent instances (a brief mount overlap during navigation) can't
// stack teardowns. Cancellation is driven by the ban-lift branch; firing after unmount is made safe by
// the fire-time getUser re-check before signOutLocal.
const bannedSignOutRunner = createCoalescingIdleRunner();

function accountRowHasId(accountData: unknown): boolean {
    return (
        typeof accountData === "object" &&
        accountData !== null &&
        "id" in accountData &&
        Boolean((accountData as { id: unknown }).id)
    );
}

// A ban is active only while banned_until is in the future. banned_until is a GoTrue field that
// isn't on the session (nor on the SDK's User type), so the only source of a current value is
// supabase.auth.getUser() — used both for the component's `user` state and for the deferred-teardown
// re-check at fire time. Takes unknown so both the `any` state and the typed getUser() user fit.
function isActiveBan(u: unknown): boolean {
    const bannedUntil = (u as { banned_until?: string | null } | null | undefined)?.banned_until;
    return !!bannedUntil && new Date(bannedUntil).getTime() > Date.now();
}

/**
 * Wrapper component to handle permissions
 * @param {Props} props - The props
 * @returns {JSX.Element}
 */
function PermissionWrapper({ children }: Props) {
    const session = useSession();
    const { impersonationSession, isImpersonating } = useImpersonation();
    const { accountData, dataLoaded } = useAccountContext();
    const [user, setUser] = useState<any>(null);
    const [isChatModalOpen, setIsChatModalOpen] = useState(false);
    // EN-1414: Settings > Organization > "Show Floating Buttons".
    const floatingButtonsHidden = accountData?.meta_data?.floating_buttons_hidden === true;

    useEffect(() => {
        setIntercomLauncherHidden(floatingButtonsHidden);
    }, [floatingButtonsHidden]);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    // Guards the banned-user branch so it can't re-fire while signOutLocal is still
    // resolving (effect re-entry on dataLoaded/accountData changes would otherwise
    // produce duplicate toasts and signOut calls).
    const bannedHandledRef = useRef(false);

    const effectiveUserId =
        isImpersonating && impersonationSession?.targetUserId ? impersonationSession.targetUserId : session?.user?.id;

    const { data: membershipsData } = useMemberships(effectiveUserId);
    const groupSession = useGroupSession();
    const { isLoading: groupLoading } = groupSession;
    // Judge membership from the latest lookup, not stale cache: a mid-session revocation returns
    // 401/403 while React Query still holds the prior successful groupMe. Without this a revoked
    // user would be force-routed to /corporate for the whole cache window — and CorporatePage's own
    // guard would then send them home, producing a redirect loop. Both guards gate on this same
    // shared derivation (see deriveGroupMembership) so they can't drift apart.
    const { isGroupMember } = deriveGroupMembership(groupSession);

    // EN-837: subscribe to recorder state so the routing effect re-runs (and re-evaluates its
    // preconditions) when a recording/upload finishes, rather than firing a stale deferred navigate.
    // Gates on hasAudioInFlight — NOT the full selectRecorderActive — on purpose: a redirect to
    // /corporate or /select-clinic only UNMOUNTS the recorder (it does not clear the session), so it's
    // unsafe only while UNPERSISTED audio is in flight (live capture or an upload still uploading). The
    // bounded flags (completing / finalizing) carry already-persisted audio, and unlike the
    // session-clearing lock/sign-out paths this redirect has NO watchdog (it re-runs declaratively on
    // this dep, not via runWhenRecorderIdle) — so gating on a bounded flag that got stuck would block
    // routing forever. A stuck live-capture/upload flag instead surfaces via the watchdog's Sentry
    // escalation, and blocking a redirect during genuine audio-in-flight is the intended protection.
    const audioInFlight = useAppSelector((state) => hasAudioInFlight(state.globalState));

    // Multi-clinic users must pick a clinic when none is stored or the stored id is no longer valid.
    // Zero-clinic users who are resolved corporate group members are routed to /corporate instead.
    useEffect(() => {
        if (!session?.user) {
            return;
        }
        if (location.pathname === `/${ENDPOINTS.SelectClinic}`) {
            return;
        }
        // Don't make any routing decision (neither the /corporate redirect nor the
        // SelectClinic redirect) until the group session has settled. Otherwise a
        // stale cached groupMe can redirect a normal clinic user during load, or a
        // dual-role/slow user can be sent to /select-clinic before group membership
        // resolves and never routed to /corporate — stuck.
        if (groupLoading) {
            return;
        }
        // Corporate group members are routed to the corporate view. v1 rule: any resolved group
        // member → /corporate. Every authenticated user gets an auto-provisioned placeholder
        // account (account/resolved and account/memberships both legacy-fall-back to the user's
        // own id), so those signals can't identify a group-only user — the resolved group (groupMe)
        // is the reliable discriminator. A normal clinic user is not a group member
        // (/group/usage/me 401s → groupMe undefined) and is unaffected. Dual-role clinic+group
        // handling (a switcher) is a deferred follow-up.
        if (isGroupMember && location.pathname !== "/corporate") {
            // EN-837: the MediaRecorder lives in AssetLibraryProvider inside PermissionWrapper, and
            // /corporate is NOT wrapped by it — navigating there unmounts the recorder mid-capture.
            // Bail while audio is in flight; this effect re-runs when that clears (audioInFlight is a
            // dep) and re-evaluates preconditions freshly, so a stale redirect can't fire after
            // membership/clinic selection changed mid-recording.
            if (audioInFlight) return;
            navigate("/corporate", { replace: true });
            return;
        }

        if (!membershipsData) {
            return;
        }

        const ids = membershipsData.account_ids ?? [];
        if (ids.length <= 1) {
            return;
        }
        const stored = getStoredSelectedClinicAccountId(effectiveUserId!);
        if (stored && ids.includes(stored)) {
            return;
        }
        // EN-837: /select-clinic is NOT wrapped by PermissionWrapper, so navigating there unmounts
        // the recorder. Bail while audio is in flight; the effect re-runs when that clears (audioInFlight
        // dep) and re-checks preconditions, so a stale redirect can't fire after state changed mid-recording.
        if (audioInFlight) return;
        navigate(`/${ENDPOINTS.SelectClinic}`, { replace: true });
    }, [
        session?.user?.id,
        isImpersonating,
        impersonationSession?.targetUserId,
        navigate,
        location.pathname,
        membershipsData,
        effectiveUserId,
        isGroupMember,
        groupLoading,
        audioInFlight,
    ]);

    const openVisit = useCallback((visitId: string) => navigate(`/appointments/${visitId}`), [navigate]);

    const handleSetupComplete = useCallback(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete("setup");
        window.location.href = url.toString();
    }, []);

    // Track checkout return in sessionStorage so it survives URL param cleanup by BillingSettings
    const [checkoutReturning, setCheckoutReturning] = useState(() => {
        if (new URLSearchParams(window.location.search).get("checkout") === "success") {
            sessionStorage.setItem("checkout_returning", "true");
            return true;
        }
        return sessionStorage.getItem("checkout_returning") === "true";
    });

    // Only fetch billing plan when we need it: self-serve accounts that might be
    // in an expired trial or returning from checkout. Avoids an API call + Stripe
    // lookup on every page load for non-self-serve accounts.
    const shouldFetchBillingPlan = !!accountData?.is_self_serve || checkoutReturning;
    const { data: billingPlan, isLoading: billingPlanLoading } = useBillingPlan({ enabled: shouldFetchBillingPlan });

    // fetch the user data specifically for the banned_until field
    useEffect(() => {
        const fetchUser = async () => {
            const {
                data: { user },
                error,
            } = await supabase.auth.getUser();

            if (error) {
                Sentry.captureException(error);
                return null;
            }

            if (!user) {
                return null;
            }

            setUser(user);
        };

        fetchUser();
    }, [location]);

    const daysLeftInTrial = billingPlan?.trial_days_remaining ?? null;

    // Auto-dismiss trial banner after 8 seconds
    useEffect(() => {
        if (daysLeftInTrial && daysLeftInTrial > 0 && !trialBannerDismissed) {
            const timer = setTimeout(() => setTrialBannerDismissed(true), 8000);
            return () => clearTimeout(timer);
        }
    }, [daysLeftInTrial, trialBannerDismissed]);

    // Get the notification banner message from the environment variables, and append the days left in the trial
    const notificationBannerMessage = import.meta.env.VITE_NOTIFICATIONS_BANNER_MESSAGE || "";
    let notificationBannerJsx = null;
    if (daysLeftInTrial && daysLeftInTrial > 0 && !trialBannerDismissed) {
        notificationBannerJsx = (
            <div className="flex-1 min-w-[240px] flex items-center justify-between">
                <div>
                    {notificationBannerMessage && <p>{notificationBannerMessage}</p>}
                    <p>
                        Your free trial ends in {daysLeftInTrial} {daysLeftInTrial !== 1 ? "days" : "day"}!{" "}
                        <button
                            className="font-semibold text-sm text-max-900 hover:text-max-900 underline"
                            onClick={() => setShowUpgradeModal(true)}
                        >
                            Upgrade now
                        </button>{" "}
                        to keep your account active.
                    </p>
                </div>
                <button
                    className="ml-4 shrink-0 p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-200/50 transition-colors"
                    aria-label="Dismiss"
                    onClick={() => setTrialBannerDismissed(true)}
                >
                    <XCloseIcon className="w-3.5 h-3.5" />
                </button>
            </div>
        );
    }

    // Check if the user has access to the self-serve features and redirect if not
    useEffect(() => {
        // If the user is not logged in, do not proceed
        if (!accountData || !session) {
            return;
        }

        // If the user is banned, log them out and redirect to the login page.
        const activeBan = isActiveBan(user);

        if (activeBan && !bannedHandledRef.current) {
            bannedHandledRef.current = true;
            toast.warning("Account is deactivated. Contact support@happydoc.ai.");
            // Local scope: the ban is server-enforced, so we only need to clear this device's session.
            // Defer navigation until signOut resolves so we don't land on /login with a still-valid token.
            // EN-837: signOutLocal clears the Supabase session, rebuilding the router with the
            // unauthenticated routes and tearing down the recorder. Defer the sign-out+nav until the
            // recorder is idle (runs synchronously when already idle) via the coalescing runner so the
            // handle can be cancelled on unmount / if the ban lifts. bannedHandledRef also blocks
            // re-entry while the deferred run is pending.
            bannedSignOutRunner.schedule(() => {
                // Re-validate at fire time. The recorder-idle deferral opens a window in which the ban
                // can be lifted (admin unban + token refresh / re-auth) with NO navigation — and `user`
                // is refetched only by the location-keyed effect above, so the cancel branch below would
                // never observe the lift and `activeBan` stays stale-true. getUser() is the only source
                // of a fresh banned_until (it isn't on the session), so re-check it here before the
                // destructive sign-out and bail if the ban is gone, re-arming for a future ban. On a
                // getUser error we fail closed (sign out) — the ban was confirmed active when scheduled,
                // matching the pre-EN-837 behavior where the sign-out fired immediately.
                void (async () => {
                    try {
                        const { data, error } = await supabase.auth.getUser();
                        if (!error && !isActiveBan(data.user)) {
                            bannedHandledRef.current = false;
                            return;
                        }
                    } catch {
                        // fall through to sign out (fail closed)
                    }
                    // Re-read the observed-lift flag before the destructive sign-out: the cancel branch
                    // (a nav refresh that saw the lift) can clear it while our getUser above was in
                    // flight, and on a getUser failure the fail-closed path would otherwise sign out a
                    // user we already know is valid. The flag is authoritative over a failed re-check.
                    if (!bannedHandledRef.current) return;
                    await signOutLocal();
                    navigate(ENDPOINTS.Login);
                })();
            });
            return;
        }

        if (!activeBan && bannedHandledRef.current) {
            // Ban lifted and observed via a navigation refresh before the deferred sign-out fired:
            // cancel the pending teardown so we don't sign out a now-valid user, and re-arm for a
            // future ban. (The fire-time re-check above is the backstop for the no-navigation path.)
            bannedSignOutRunner.cancel();
            bannedHandledRef.current = false;
        }

        // If the account data is not loaded, OR the user is not self-serve, do not proceed
        if (!dataLoaded || !accountData?.is_self_serve) return;
    }, [dataLoaded, accountData, navigate, session, user]);

    // Clear the checkout flag once billing plan has loaded (any type). If the user upgraded,
    // billing_type will be non-trial. If they abandoned checkout, billing_type stays "trial"
    // and clearing the flag allows trial-expired detection to kick in on the same data.
    useEffect(() => {
        if (checkoutReturning && billingPlan != null) {
            sessionStorage.removeItem("checkout_returning");
            setCheckoutReturning(false);
        }
    }, [checkoutReturning, billingPlan]);

    // When returning from checkout with status already "Trial Expired", we must wait for billing
    // to confirm before showing overlay; otherwise we could lock out a paying customer.
    const waitingForBillingAfterCheckout =
        checkoutReturning && !!accountData?.is_self_serve && accountData?.status === AccountStatus.TrialExpired;

    // "Might be expired trial" = we need the billing API to confirm. Only for self-serve
    // accounts where trial_end_date is in the past. Excluded statuses already have a
    // known outcome (see isTrialExpiredByStatus below).
    const mightBeExpiredTrial = (() => {
        if (!dataLoaded || !accountData?.is_self_serve) return false;
        if (accountData.status == null) return false;
        // Active = paid subscriber, Lost = cancelled (billing_type reset to trial on deletion,
        // but they're not a trial user). Trial Expired = handled by isTrialExpiredByStatus below.
        const excludedStatuses: AccountStatus[] = [
            AccountStatus.Active,
            AccountStatus.Lost,
            AccountStatus.TrialExpired,
        ];
        if (excludedStatuses.includes(accountData.status as AccountStatus)) return false;
        if (!accountData.trial_end_date) return false;
        if (new Date(accountData.trial_end_date) >= new Date()) return false;
        if (checkoutReturning) return false;
        return true;
    })();

    // Show spinner when we need billing to decide (pre-cron maybe-expired or post-checkout status).
    if ((mightBeExpiredTrial || waitingForBillingAfterCheckout) && billingPlanLoading) {
        return (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-white">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-max-500" />
            </div>
        );
    }

    // Detect expired trial in two cases:
    // 1. Cron has run: account status is "Trial Expired" — but NOT when returning from Stripe
    //    checkout; in that case we defer to billing so we don't lock out a paying customer.
    // 2. Pre-cron or post-checkout: billing plan confirms still on trial (mightBeExpiredTrial
    //    or returning from checkout with status Trial Expired).
    // If the billing API failed for case 2, fail open — paying customers must never be locked out.
    //
    // When billing data shows a non-trial plan, trust it over status. This prevents showing the
    // overlay after checkout when the webhook has updated billing but not yet account status.
    const isTrialExpiredByStatus =
        !!accountData?.is_self_serve && accountData?.status === AccountStatus.TrialExpired && !checkoutReturning;
    const isTrialExpiredByBilling =
        (mightBeExpiredTrial || waitingForBillingAfterCheckout) && billingPlan?.billing_type === "trial";
    const billingConfirmsUpgrade = billingPlan != null && billingPlan.billing_type !== "trial";
    const isTrialExpired = !billingConfirmsUpgrade && (isTrialExpiredByStatus || isTrialExpiredByBilling);

    if (isTrialExpired) {
        return <TrialExpiredOverlay />;
    }

    // First-login setup flow for self-serve accounts that haven't completed profile
    const isSetupPreview =
        import.meta.env.DEV && new URLSearchParams(window.location.search).get("setup") === "preview";
    const accountHydratedEnough = dataLoaded || accountRowHasId(accountData);
    const needsSetup =
        isSetupPreview || (accountHydratedEnough && accountData?.is_self_serve && !accountData?.has_completed_profile);

    // While account data is loading, show nothing to prevent a flash of the main
    // app before the setup flow renders for self-serve users. If we already have
    // an account id (e.g. refetch after password), render the shell so LockScreenOverlay
    // can show PIN setup instead of an indefinite global spinner.
    if (!accountHydratedEnough && !isSetupPreview) {
        return (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-50">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-max-500" />
            </div>
        );
    }

    if (needsSetup) {
        return <SetupFlow onComplete={handleSetupComplete} />;
    }

    return (
        <>
            <SkipLink />
            <SRAnnouncementProvider>
                <CacheBuster>
                    {/* MicProvider never opens the mic while idle (permission query and enumerateDevices only), so
                        mounting it app-wide is cheap. It must wrap AssetLibraryProvider for the record-press gate. */}
                    <MicProvider>
                        <AssetLibraryProvider openVisit={openVisit}>
                            {/* EN-1492: app-wide so the recording widget's Stop & Generate can ask about edits; PatientView's
                                own KeepEditsProvider defers to this one, so both share one busy guard and one dialog. */}
                            <KeepEditsProvider>
                                <ReviewSessionGuard />
                                <TourProvider>
                                    <TourBlockerRegistrations />
                                    <RecordingWidget />
                                    <MergeVisitsDialogHost />
                                    {accountData?.phone_calls_enabled && <PhoneCallWidget />}
                                    {/* ErrorBoundary wraps only tour UI (TourEngine/TourDevTools), not main content.
						     A tour crash renders TourCrashFallback (restores scroll, renders nothing visible); the app continues.
						     TourProvider remains intact above this boundary so useTour() still works for all content. */}
                                    <Sentry.ErrorBoundary
                                        fallback={<TourCrashFallback />}
                                        beforeCapture={(scope) => scope.setTag("feature", "product-tours")}
                                        onError={(error) => {
                                            console.error("[Tours] Tour system crashed:", error);
                                            document.documentElement.style.overflow = "";
                                        }}
                                    >
                                        <TourEngine />
                                        {import.meta.env.DEV && <TourDevTools />}
                                    </Sentry.ErrorBoundary>
                                    <AppBlur>
                                        <div>
                                            <div className="sticky left-0 right-0 top-0 z-50 navbar-selector">
                                                {/* Notification Banner */}
                                                {(notificationBannerMessage || notificationBannerJsx) && (
                                                    <NotificationBanner
                                                        message={notificationBannerMessage}
                                                        messageJsx={notificationBannerJsx}
                                                    />
                                                )}
                                                {/* Top Nav Bar */}
                                                <Navbar />
                                            </div>
                                            {/* Main content */}
                                            <main id="main-content">
                                                {/* Bottom gutter so every page can scroll its last controls clear of the
                                            floating bottom-right buttons (EN-1414): Quick Start's minimized button tops
                                            out at 204px on the home page, Scout at 148px elsewhere. Home keeps its
                                            original 140px for an in-progress recording when the buttons are hidden. */}
                                                <div
                                                    className={
                                                        location.pathname === "/"
                                                            ? floatingButtonsHidden
                                                                ? "pb-[140px]"
                                                                : "pb-[212px]"
                                                            : floatingButtonsHidden
                                                              ? ""
                                                              : "pb-[156px]"
                                                    }
                                                >
                                                    {children}
                                                </div>
                                                <div className="hidden happydoc-account-id">{accountData?.id}</div>
                                            </main>
                                        </div>

                                        {/* HappyDoc Mascot Sticker - Bottom Right */}
                                        {!floatingButtonsHidden && (
                                            <div
                                                className="fixed bottom-[68px] right-1.5 z-40"
                                                data-tour="scout-mascot"
                                            >
                                                {isHalloween() ? (
                                                    // EN-1476: same 80px box as the SVG, which bakes in its own
                                                    // shadow; the PNG has none, so it comes from drop-shadow here.
                                                    // A PDF dragged over the image fires dragenter there, which bubbles
                                                    // to the button and opens Scout. draggable={false} only stops the
                                                    // image itself from being picked up.
                                                    <button
                                                        type="button"
                                                        aria-label="Open Scout"
                                                        className="focused block h-20 w-20 p-0 border-0 bg-transparent rounded-full hover:scale-110 transition-transform duration-200 cursor-pointer"
                                                        onClick={() => setIsChatModalOpen(!isChatModalOpen)}
                                                        onDragEnter={() => setIsChatModalOpen(true)} // For dragging PDFs into Scout
                                                    >
                                                        <img
                                                            src={HALLOWEEN_SCOUT_SRC}
                                                            alt=""
                                                            draggable={false}
                                                            className="h-20 w-20 drop-shadow-[0_2px_8px_rgba(0,0,0,0.2)]"
                                                        />
                                                    </button>
                                                ) : (
                                                    <HappyDocMascot
                                                        className="h-20 w-20 hover:scale-110 transition-transform duration-200 cursor-pointer"
                                                        onClick={() => setIsChatModalOpen(!isChatModalOpen)}
                                                        onDragEnter={() => setIsChatModalOpen(true)} // For dragging PDFs into Scout
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {/* Scout Chat */}
                                        <ScoutChat isOpen={isChatModalOpen} onClose={() => setIsChatModalOpen(false)} />
                                    </AppBlur>
                                </TourProvider>
                            </KeepEditsProvider>
                        </AssetLibraryProvider>
                    </MicProvider>
                </CacheBuster>
                {/* Only mount when open so Stripe/billing fetches run only when the user opens the modal */}
                {showUpgradeModal && (
                    <PlanUpgradeModal
                        isOpen={true}
                        onClose={() => setShowUpgradeModal(false)}
                        currentTier="trial"
                        billingType="trial"
                    />
                )}
            </SRAnnouncementProvider>
        </>
    );
}

export default PermissionWrapper;
